import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Lightbulb, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';
import { getAppointmentContext } from '@/services/ai-context.service';
import { FormattedText } from '@/components/ui/FormattedText';

interface Props {
  appointmentId: number;
  /**
   * This endpoint generates its summary with an LLM call, so it stays off
   * until the user asks the assistant for something.
   */
  enabled?: boolean;
}

const RISK_COLORS = {
  low: 'bg-green-50 text-green-700 ring-green-200',
  medium: 'bg-amber-50 text-amber-700 ring-amber-200',
  high: 'bg-red-50 text-red-700 ring-red-200',
} as const;

export function AppointmentContextPanel({ appointmentId, enabled = false }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-context', 'appointment', appointmentId],
    queryFn: () => getAppointmentContext(appointmentId),
    staleTime: 30_000,
    enabled,
  });

  if (!enabled) return null;

  if (isLoading) return <SkeletonLoader />;

  if (isError || !data) {
    return (
      <div className="p-4 text-sm text-red-600">
        Failed to load appointment context. Please try again.
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {/* AI Summary */}
      <div className="p-4">
        <div className="rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 p-4 text-white">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-indigo-200 mb-2">
            AI Summary
          </h3>
          <FormattedText text={data.aiSummary} className="text-sm leading-relaxed" />
        </div>
      </div>

      {/* Patient Quick Info */}
      {data.patientSummary && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <User className="h-4 w-4 text-blue-500" />
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Patient Info
            </h4>
          </div>
          <div className="rounded-md border border-slate-100 bg-slate-50 p-3">
            <p className="text-sm font-medium text-slate-800 mb-2">{data.patientSummary.name}</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xs text-slate-400">Visits</div>
                <div className="text-sm font-semibold text-slate-800">{data.patientSummary.totalVisits}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">No-Shows</div>
                <div
                  className={cn(
                    'text-sm font-semibold',
                    data.patientSummary.noShowCount > 2 ? 'text-red-600' : 'text-slate-800',
                  )}
                >
                  {data.patientSummary.noShowCount}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Spent</div>
                <div className="text-sm font-semibold text-slate-800">
                  {formatCurrency(data.patientSummary.totalSpent)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No-Show Risk */}
      {data.noShowHistory && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              No-Show History
            </h4>
          </div>
          {(() => {
            const risk = data.noShowHistory.count > 3 ? 'high' : data.noShowHistory.count > 1 ? 'medium' : 'low';
            return (
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ring-1 ring-inset',
                  RISK_COLORS[risk],
                )}
              >
                {risk.charAt(0).toUpperCase() + risk.slice(1)} Risk ({data.noShowHistory.count} no-show{data.noShowHistory.count !== 1 ? 's' : ''})
              </span>
            );
          })()}
        </div>
      )}

      {/* Suggestions */}
      {(data.suggestions ?? []).length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Suggestions
            </h4>
          </div>
          <ul className="space-y-2">
            {(data.suggestions ?? []).map((suggestion, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="p-4 space-y-4 animate-pulse">
      <div className="rounded-lg bg-gradient-to-br from-indigo-200 to-purple-200 h-28" />
      <div className="rounded-md bg-slate-100 h-24" />
      <div className="space-y-2">
        <div className="h-3 w-24 bg-slate-200 rounded" />
        <div className="h-8 w-20 bg-slate-100 rounded-full" />
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-24 bg-slate-200 rounded" />
          <div className="h-4 w-full bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}
